import { useState, useRef, useEffect } from 'react';
import './OmniConcierge.css';

const CAPABILITIES = [
  { label: 'Bridge service questions', status: 'ready', detail: 'Answers from site context' },
  { label: 'Business hours', status: 'ready', detail: 'Configured response only' },
  { label: 'Consultation handoff', status: 'ready', detail: 'Directs visitors to contact Ryan' },
  { label: 'Calendar scheduling', status: 'limited', detail: 'Not connected — fails closed' },
  { label: 'CRM writes', status: 'limited', detail: 'Not connected — no fake saves' },
  { label: 'Live transfer', status: 'limited', detail: 'Not connected — collects details' },
];

const INITIAL_CAPTION = {
  speaker: 'OmniConcierge',
  text: 'Ready to connect. Start the demo to see live captions, safe-action status, and realtime protocol events.',
};

const INITIAL_ACTION = {
  intent: 'Standing by',
  action: 'No session active',
  safety: 'No data is stored by this caption HUD.',
  tone: 'idle',
};

const detectAction = (text = '') => {
  const normalized = text.toLowerCase();

  if (/(schedule|appointment|calendar|book|meeting|call)/.test(normalized)) {
    return {
      intent: 'Consultation or scheduling request',
      action: 'Collect details and direct to the contact form',
      safety: 'Calendar is not connected, so the demo will not pretend to book anything.',
      tone: 'limited',
    };
  }

  if (/(crm|save|record|database|lead|contact)/.test(normalized)) {
    return {
      intent: 'Lead capture or CRM request',
      action: 'Explain the contact path and no-store policy',
      safety: 'CRM writes are disabled until a real production integration is connected.',
      tone: 'limited',
    };
  }

  if (/(hour|open|available|timezone)/.test(normalized)) {
    return {
      intent: 'Business-hours question',
      action: 'Use configured business-hours response',
      safety: 'Safe informational response only.',
      tone: 'ready',
    };
  }

  if (/(service|workflow|automation|ai|data|business|offer)/.test(normalized)) {
    return {
      intent: 'Bridge services question',
      action: 'Answer from Bridge site context',
      safety: 'No external tool action needed.',
      tone: 'ready',
    };
  }

  return {
    intent: 'General visitor question',
    action: 'Respond conversationally from Bridge context',
    safety: 'No real-world action taken unless a connected tool confirms it.',
    tone: 'ready',
  };
};

export const OmniConcierge = () => {
  const [status, setStatus] = useState('idle');
  const [logs, setLogs] = useState(['> Terminal loaded. Standing by for voice hook.']);
  const [caption, setCaption] = useState(INITIAL_CAPTION);
  const [captionHistory, setCaptionHistory] = useState([]);
  const [actionHud, setActionHud] = useState(INITIAL_ACTION);
  const [liveState, setLiveState] = useState('Ready');

  const pcRef = useRef(null);
  const audioRef = useRef(null);
  const dcRef = useRef(null);
  const captionDraftRef = useRef('');
  const micStreamRef = useRef(null);

  const addLog = (msg) => setLogs(prev => [...prev.slice(-15), `> ${msg}`]);

  const addCaption = (speaker, text) => {
    const cleaned = text?.trim();
    if (!cleaned) return;

    const nextCaption = { speaker, text: cleaned };
    setCaption(nextCaption);
    setCaptionHistory(prev => [nextCaption, ...prev].slice(0, 4));

    if (speaker === 'OmniConcierge') {
      setActionHud(detectAction(cleaned));
    }
  };

  const updateLiveState = (nextState, logMessage) => {
    setLiveState(nextState);
    if (logMessage) addLog(logMessage);
  };

  const handleRealtimeEvent = (event) => {
    switch (event.type) {
      case 'input_audio_buffer.speech_started':
        updateLiveState('Listening', 'Visitor speech detected.');
        setActionHud(prev => ({ ...prev, intent: 'Listening to visitor', action: 'Waiting for the full request', tone: 'active' }));
        break;
      case 'input_audio_buffer.speech_stopped':
        updateLiveState('Understanding', 'Visitor paused. Interpreting request...');
        break;
      case 'conversation.item.input_audio_transcription.completed':
      case 'conversation.item.input_audio_transcription.done':
        addCaption('Visitor', event.transcript || event.text || '');
        break;
      case 'response.created':
        updateLiveState('Thinking', 'Realtime model is preparing a response...');
        break;
      case 'response.audio_transcript.delta':
        captionDraftRef.current += event.delta || '';
        if (captionDraftRef.current.trim()) {
          setCaption({ speaker: 'OmniConcierge', text: captionDraftRef.current.trim() });
        }
        updateLiveState('Speaking');
        break;
      case 'response.audio_transcript.done':
        addLog(`Omni: "${event.transcript}"`);
        addCaption('OmniConcierge', event.transcript || captionDraftRef.current);
        captionDraftRef.current = '';
        updateLiveState('Listening');
        break;
      case 'response.done':
        captionDraftRef.current = '';
        updateLiveState('Listening', 'Response complete. VAD is listening...');
        break;
      case 'error':
        updateLiveState('Error', event.error?.message || 'Realtime API returned an error.');
        setActionHud({
          intent: 'Realtime error',
          action: 'Show safe error and allow retry',
          safety: 'No user transcript is stored by the HUD.',
          tone: 'error',
        });
        break;
      default:
        break;
    }
  };

  const startCall = async () => {
    try {
      setStatus('connecting');
      setCaption({ speaker: 'System', text: 'Opening secure realtime voice session...' });
      setActionHud({
        intent: 'Connection startup',
        action: 'Request microphone permission and create WebRTC tunnel',
        safety: 'Safe mode active. Unconnected tools fail closed.',
        tone: 'active',
      });
      updateLiveState('Connecting', 'Authenticating with Tier-1 Security Gateway...');

      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      pc.ontrack = (e) => {
        updateLiveState('Receiving audio', 'Receiving incoming audio API track!');
        if (audioRef.current) {
          audioRef.current.srcObject = e.streams[0];
        }
      };

      updateLiveState('Mic permission', 'Requesting local microphone access...');
      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = micStream;
      pc.addTrack(micStream.getTracks()[0]);
      addLog('Microphone hooked.');

      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;

      dc.addEventListener('open', () => {
        updateLiveState('Listening', 'DataChannel connected. VAD is listening...');
        setCaption({ speaker: 'System', text: 'Connection live. Ask about Bridge services, hours, or consultations.' });
        dc.send(JSON.stringify({
          type: 'response.create',
          response: { instructions: 'Introduce yourself as OmniConcierge.' },
        }));
      });

      dc.addEventListener('message', (e) => {
        try {
          handleRealtimeEvent(JSON.parse(e.data));
        } catch (err) {
          console.error('DataChannel parse error:', err);
        }
      });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpOffer = pc.localDescription?.sdp || offer.sdp;

      console.log('SDP offer length:', sdpOffer?.length);
      console.log('SDP offer start:', JSON.stringify(sdpOffer?.slice(0, 50)));

      if (!sdpOffer || !sdpOffer.startsWith('v=0')) {
        throw new Error(
          `Invalid SDP offer before sending. Start was: ${JSON.stringify(
            sdpOffer?.slice(0, 50),
          )}`,
        );
      }

      addLog('SDP Built. Initiating secure tunnel...');

      const response = await fetch('/api/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/sdp',
        },
        body: sdpOffer,
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(`Realtime session failed: ${response.status} ${responseText}`);
      }

      await pc.setRemoteDescription({
        type: 'answer',
        sdp: responseText,
      });

      updateLiveState('Listening', 'TUNNEL ESTABLISHED. Talk now!');
      setStatus('connected');
    } catch (err) {
      console.error('WebRTC Error:', err);
      setStatus('error');
      setLiveState('Error');
      setCaption({ speaker: 'System', text: 'The realtime demo could not connect. Check the setup and retry.' });
      setActionHud({
        intent: 'Connection failed',
        action: 'Show retry path',
        safety: err.message,
        tone: 'error',
      });
      addLog(`FATAL ERROR: ${err.message}`);
    }
  };

  const endCall = () => {
    if (pcRef.current) pcRef.current.close();
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }
    pcRef.current = null;
    dcRef.current = null;
    captionDraftRef.current = '';
    setStatus('idle');
    setLiveState('Ready');
    setCaption({ speaker: 'System', text: 'Tunnel closed safely. No transcript was stored.' });
    setActionHud(INITIAL_ACTION);
    addLog('Tunnel closed safely.');
  };

  useEffect(() => {
    return () => {
      if (pcRef.current) pcRef.current.close();
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return (
    <div className="concierge-module">
      <audio ref={audioRef} autoPlay playsInline style={{ display: 'none' }} />

      <div className="concierge-header">
        <div>
          <h2>🎙️ The OmniConcierge</h2>
          <p>Live WebRTC Autonomous Receptionist Protocol</p>
        </div>
        <div className={`live-state ${status}`} aria-live="polite">
          <span className="live-dot" />
          {liveState}
        </div>
      </div>

      <div className="concierge-controls">
        {status === 'idle' && (
          <button className="call-btn activate" onClick={startCall}>
            <span className="pulse-dot"></span> Initiate WebRTC Hook
          </button>
        )}
        {status === 'connecting' && (
          <button className="call-btn connecting" disabled>Handshaking...</button>
        )}
        {status === 'connected' && (
          <button className="call-btn terminate" onClick={endCall}>SEVER CONNECTION</button>
        )}
        {status === 'error' && (
          <button className="call-btn error" onClick={startCall}>Retry Architecture</button>
        )}
      </div>

      <section className={`caption-stage ${status}`} aria-label="Live AI captions" aria-live="polite">
        <div className="caption-orb" aria-hidden="true">
          <span />
        </div>
        <div className="caption-content">
          <div className="caption-kicker">Live Caption</div>
          <p className="caption-speaker">{caption.speaker}</p>
          <p className="caption-text">“{caption.text}”</p>
        </div>
      </section>

      <div className="concierge-hud-grid">
        <section className={`action-hud ${actionHud.tone}`} aria-label="Current AI action status">
          <div className="hud-eyebrow">Action HUD</div>
          <h3>{actionHud.intent}</h3>
          <dl>
            <div>
              <dt>Current move</dt>
              <dd>{actionHud.action}</dd>
            </div>
            <div>
              <dt>Safety status</dt>
              <dd>{actionHud.safety}</dd>
            </div>
          </dl>
        </section>

        <section className="capability-matrix" aria-label="Demo capabilities">
          <div className="hud-eyebrow">Capability Matrix</div>
          <div className="capability-list">
            {CAPABILITIES.map((capability) => (
              <div className={`capability-row ${capability.status}`} key={capability.label}>
                <span className="capability-status">{capability.status === 'ready' ? '✓' : '!'}</span>
                <div>
                  <strong>{capability.label}</strong>
                  <small>{capability.detail}</small>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {captionHistory.length > 0 && (
        <section className="caption-history" aria-label="Recent in-session captions">
          <div className="terminal-header">Recent Captions — In Browser Only</div>
          {captionHistory.map((item, index) => (
            <div className="history-line" key={`${item.speaker}-${index}-${item.text.slice(0, 12)}`}>
              <span>{item.speaker}</span>
              <p>{item.text}</p>
            </div>
          ))}
        </section>
      )}

      <div className="concierge-terminal">
        <div className="terminal-header">Realtime API Packet Sniffer</div>
        <div className="terminal-body" style={{ flexDirection: 'column-reverse' }}>
          {logs.slice().reverse().map((line, i) => (
            <div key={i} className="log-line" style={{ color: line.includes('ERROR') ? '#fca5a5' : '#10b981' }}>{line}</div>
          ))}
        </div>
      </div>
    </div>
  );
};
